import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { SUPPORTED_LANG_CODES, type SupportedLangCode } from '@/application/i18n/language'
import type {
  LocaleTranslationState,
  TranslationsWritePayload,
  TaxonomyTerm,
} from '@/domains/types'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/ui/components/common/Tabs'
import { Badge } from '@/ui/components/common/Badge'
import { Label } from '@/ui/components/common/Label'
import { Input } from '@/ui/components/common/Input'
import { RichTextEditor } from '@/ui/components/common/RichTextEditor'
import { TermLabelsEditor } from '@/ui/components/translations/TermLabelsEditor'
import { classNames } from '@/ui/utils/main'

const LOCALE_FLAG: Record<SupportedLangCode, string> = { fr: '🇫🇷', en: '🇬🇧', es: '🇪🇸' }

interface TranslatableFieldSpec {
  /** Logical field name — matches the backend registry (`name`/`title`, `description`, `url`).
   * For `kind: 'terms'` this is purely a React key. */
  name: string
  labelKey: string
  /** `input` for a plain title/name field, `richtext` for description, `url`
   * for a single-line link input (fact's source url — non-gating), `terms`
   * for the investigation's category terms (non-gating: one label input per
   * linked term — see `terms`). */
  kind: 'input' | 'richtext' | 'url' | 'terms'
  /** The current source-language value, shown read-only under every translation field.
   * Unused for `kind: 'terms'` (each term shows its own reference text). */
  sourceValue: string
  /**
   * Matches the `scrollable`/`compact` props the caller passes to its own
   * source-field `RichTextEditor`, so the translation editor renders at the
   * same height instead of always falling back to the compact default.
   */
  richTextEditorProps?: { scrollable?: boolean; compact?: boolean }
  /** `kind: 'terms'` only: the terms currently linked to the investigation
   * (managed by the source tab's picker). A translation tab can only type the
   * label of each of them: nothing can be linked or unlinked from there. */
  terms?: readonly TaxonomyTerm[]
}

interface TranslationTabsProps {
  sourceLocale: SupportedLangCode
  fields: TranslatableFieldSpec[]
  /** Per-locale state fetched from `GET /translations/:type/:id` — used to
   * seed the tab badges and initial field values. `undefined` while loading. */
  entityState: LocaleTranslationState[] | undefined
  /** Called on every change with the full `translations` payload ready for
   * the extended `PATCH` (TRANSLATION-PLAN.md §5). */
  onChange: (translations: TranslationsWritePayload) => void
  /**
   * The category term labels edited so far, `{ [locale]: { [termId]: label } }`
   * (controlled: owned by the caller, which turns it into the `categoryLabels`
   * payload on submit — a separate channel from `onChange` above, since a label
   * belongs to a term, not to a field). Only relevant when `fields` includes a
   * `kind: 'terms'` entry; omit otherwise.
   */
  categoryLabelValues?: Record<string, Record<string, string>>
  /** Called when the user types the label of `termId` in `locale`'s tab. */
  onCategoryLabelChange?: (locale: SupportedLangCode, termId: string, label: string) => void
  disabled?: boolean
  /**
   * Renders the caller's actual, editable source-language fields (e.g. the
   * `name`/`description` `FormField`s for investigations, plain
   * `useState`-bound inputs for leads, `register`/`Controller` fields for
   * facts). Kept as a render-prop so this component stays agnostic of
   * whichever form mechanism the caller uses — it only decides WHEN to show
   * them (the source tab is active), never HOW they're built or bound.
   */
  renderSourceFields: () => ReactNode
}

/**
 * Language tabs with state badges, shared by the investigation/lead/fact
 * edit forms (TRANSLATION-PLAN.md §5, decisions 2-6). All three supported
 * locales always render (decision 3). Exactly one field area renders below
 * the tab strip at a time: the source tab hosts the caller's real, editable
 * source fields (via `renderSourceFields`, decision 2 — "the source tab
 * edits the content row" through whatever form mechanism that entity type
 * already uses); every other tab hosts the translation inputs for that
 * locale plus the read-only source text beneath them. This component owns
 * only the active-tab state and the translation values, and reports the
 * latter back as a `translations` map on every change (decision 6: "one
 * submit for everything").
 */
export function TranslationTabs({
  sourceLocale,
  fields,
  entityState,
  onChange,
  categoryLabelValues,
  onCategoryLabelChange,
  disabled,
  renderSourceFields,
}: TranslationTabsProps) {
  const { t } = useTranslation()

  // locale -> fieldName -> current edited value (every kind except 'terms')
  const [values, setValues] = useState<Record<string, Record<string, string>>>({})
  // Seed edited values from the fetched entity state exactly once per load —
  // further edits are local until submit.
  useEffect(() => {
    if (!entityState) return
    setValues((previous) => {
      const seeded: Record<string, Record<string, string>> = { ...previous }
      for (const localeState of entityState) {
        if (seeded[localeState.locale]) continue
        const fieldValues: Record<string, string> = {}
        for (const field of fields) {
          if (field.kind === 'terms') continue
          fieldValues[field.name] = localeState.fields[field.name]?.value ?? ''
        }
        seeded[localeState.locale] = fieldValues
      }
      return seeded
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityState])

  const setFieldValue = (locale: SupportedLangCode, fieldName: string, value: string) => {
    setValues((previous) => {
      const next = {
        ...previous,
        [locale]: { ...previous[locale], [fieldName]: value },
      }
      // Report the new payload to the caller outside the `setValues`
      // updater — React may invoke an updater function more than once for a
      // single state update, and calling a DIFFERENT component's setState
      // (the caller's `onChange`/`setTranslations`) from inside it is an
      // anti-pattern React itself warns about ("Cannot update a component
      // while rendering a different component"). Scheduling it via a
      // microtask keeps `onChange` a plain side effect, called exactly once
      // per edit, after this component's own state has settled.
      queueMicrotask(() => onChange(toPayload(next)))
      return next
    })
  }

  const stateFor = (locale: SupportedLangCode) =>
    entityState?.find((localeState) => localeState.locale === locale)

  return (
    <Tabs defaultValue={sourceLocale} className="space-y-4">
      <TabsList>
        <TabsTrigger value={sourceLocale}>
          {LOCALE_FLAG[sourceLocale]} {t('translations.tab_source')}
        </TabsTrigger>
        {SUPPORTED_LANG_CODES.filter((locale) => locale !== sourceLocale).map((locale) => {
          const state = stateFor(locale)
          return (
            <TabsTrigger key={locale} value={locale}>
              <span className="flex items-center gap-1.5">
                {LOCALE_FLAG[locale]} {locale.toUpperCase()}
                <TranslationTabBadge state={state} />
              </span>
            </TabsTrigger>
          )
        })}
      </TabsList>

      <TabsContent value={sourceLocale} className="space-y-4">
        {renderSourceFields()}
      </TabsContent>

      {SUPPORTED_LANG_CODES.filter((locale) => locale !== sourceLocale).map((locale) => (
        <TabsContent key={locale} value={locale} className="space-y-4">
          {fields.map((field) => {
            if (field.kind === 'terms') {
              return (
                <div key={field.name} className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Label>{t(field.labelKey)}</Label>
                    {/* Category labels are always non-gating (TRANSLATION-PLAN.md
                        Section 8): an unlabelled term falls back to its canonical
                        name, so leaving every input empty is always valid. */}
                    <Badge
                      variant="outline"
                      className="text-muted-foreground border-border text-[10px] px-1.5 py-0"
                    >
                      {t('translations.field_optional')}
                    </Badge>
                  </div>
                  <TermLabelsEditor
                    terms={[...(field.terms ?? [])]}
                    locale={locale}
                    referenceLocale={sourceLocale}
                    values={categoryLabelValues?.[locale] ?? {}}
                    onChange={(termId, label) => onCategoryLabelChange?.(locale, termId, label)}
                    disabled={disabled}
                  />
                </div>
              )
            }

            const fieldState = stateFor(locale)?.fields[field.name]
            const currentValue = values[locale]?.[field.name] ?? ''
            return (
              <div key={field.name} className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Label>{t(field.labelKey)}</Label>
                  {fieldState?.gating === false && (
                    <Badge
                      variant="outline"
                      className="text-muted-foreground border-border text-[10px] px-1.5 py-0"
                    >
                      {t('translations.field_optional')}
                    </Badge>
                  )}
                  {fieldState?.stale && (
                    <Badge variant="outline" className="text-amber-600 border-amber-400 gap-1">
                      <AlertTriangle className="size-3" />
                      {t('translations.field_stale')}
                    </Badge>
                  )}
                </div>
                {field.kind === 'input' || field.kind === 'url' ? (
                  <Input
                    type={field.kind === 'url' ? 'url' : 'text'}
                    value={currentValue}
                    onChange={(event) => setFieldValue(locale, field.name, event.target.value)}
                    disabled={disabled}
                  />
                ) : (
                  <RichTextEditor
                    value={currentValue}
                    onChange={(next) => setFieldValue(locale, field.name, next)}
                    disabled={disabled}
                    compact={field.richTextEditorProps?.compact ?? true}
                    scrollable={field.richTextEditorProps?.scrollable}
                  />
                )}
                <div className="text-sm text-muted-foreground border-l-2 border-border pl-2">
                  <span className="font-medium">
                    {t('translations.source_field_label', { locale: sourceLocale.toUpperCase() })}
                    {': '}
                  </span>
                  <span className="whitespace-pre-wrap">{field.sourceValue}</span>
                </div>
              </div>
            )
          })}
        </TabsContent>
      ))}
    </Tabs>
  )
}

function toPayload(values: Record<string, Record<string, string>>): TranslationsWritePayload {
  const payload: TranslationsWritePayload = {}
  for (const [locale, fieldValues] of Object.entries(values)) {
    payload[locale] = { ...fieldValues }
  }
  return payload
}

function TranslationTabBadge({ state }: { state: LocaleTranslationState | undefined }) {
  const { t } = useTranslation()

  if (!state || state.missingFields > 0) {
    return (
      <span className={classNames('text-xs', state?.complete ? '' : 'text-muted-foreground')}>
        {state ? t('translations.badge_missing', { count: state.missingFields }) : ''}
      </span>
    )
  }

  const hasStale = Object.values(state.fields).some((field) => field.stale)
  if (hasStale) {
    return (
      <Badge variant="outline" className="text-amber-600 border-amber-400 text-[10px] px-1.5 py-0">
        {t('translations.badge_stale')}
      </Badge>
    )
  }

  return (
    <Badge
      variant="outline"
      className="text-emerald-600 border-emerald-400 text-[10px] px-1.5 py-0"
    >
      {t('translations.badge_complete')}
    </Badge>
  )
}
